import axios from 'axios'
import type { CreateCustomerRequestData } from '../types/notification.types'

/**
 * Axios instance configured with base URL and authorization header from localStorage.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '', // Use empty string for relative URLs with proxy
  timeout: 30000, // Increased to 30s due to slow backend /api/tables/{id}/orders endpoint
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Custom params serializer for Spring Boot compatibility
  // Spring expects: status=PENDING&status=ACCEPTED (not status[]=PENDING&status[]=ACCEPTED)
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    }
  }
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    const guestSession = localStorage.getItem('guestSession')

    // Priority: Regular user token over guest session
    // If user is logged in, NEVER use guest session headers (prevents logout bug)
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
      console.log(`🔐 API Request: ${config.method?.toUpperCase()} ${config.url} with user token`)
      return config
    }

    // Only use guest session if user is NOT logged in
    if (guestSession && config.headers) {
      try {
        const session = JSON.parse(guestSession)
        if (session.sessionToken && session.verified && session.isActive) {
          // For guest sessions, use X-Guest-Session-Token header
          config.headers['X-Guest-Session-Token'] = session.sessionToken
          config.headers.Authorization = `Bearer ${session.sessionToken}`
          console.log(`🔐 API Request: ${config.method?.toUpperCase()} ${config.url} with guest session token`)
          return config
        }
      } catch (e) {
        console.warn('Failed to parse guest session:', e)
      }
    }

    // No authentication available
    console.warn(`⚠️ API Request: ${config.method?.toUpperCase()} ${config.url} without authentication`)

    return config
  },
  (error) => {
    console.error('❌ API Request Error:', error)
    return Promise.reject(error)
  }
)

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    // Check if we received HTML instead of JSON
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('text/html') && response.config.responseType !== 'document') {
      console.error('🚫 Received HTML response instead of JSON - likely an authentication issue');
      
      // Create a proper error response
      const error = {
        response: {
          status: 401,
          data: {
            message: 'Authentication required. Please log in again.',
            isHtmlResponse: true
          }
        },
        config: response.config
      };
      
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    // Check if error response is HTML (often login pages)
    const contentType = error.response?.headers?.['content-type'] || '';
    if (contentType.includes('text/html')) {
      console.error('🚫 Received HTML error response - likely redirected to login page');
      
      // Override the error message
      error.response.data = {
        message: 'Authentication required. Please log in again.',
        isHtmlResponse: true,
        originalHtml: error.response.data
      };
    }
    
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      console.error('🚫 Authentication failed (401)')

      // Check if this is a guest auth endpoint - don't redirect
      const isGuestAuthEndpoint = error.config?.url?.includes('/guest-auth/');
      if (isGuestAuthEndpoint) {
        return Promise.reject(error);
      }

      // Check if we're already on a login page to avoid redirect loops
      const currentPath = window.location.pathname;
      if (currentPath.includes('/login')) {
        return Promise.reject(error);
      }

      // Don't redirect on public pages that handle auth failures gracefully
      if (currentPath.includes('/order-success/') ||
          currentPath.includes('/guest-order-tracking/') ||
          currentPath.startsWith('/menu') ||
          currentPath.startsWith('/qr') ||
          currentPath.startsWith('/scan') ||
          currentPath.includes('/whatsapp-simulator')) {
        console.log('On public page, not redirecting on 401 - letting page handle gracefully');
        return Promise.reject(error);
      }

      // Clear auth and redirect to login
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('guestSession')

      console.log('Session expired. Redirecting to login page...')
      window.location.href = '/login'
    }

    // Handle 403 Forbidden - session expired or access denied
    if (error.response?.status === 403) {
      console.error('🚫 Access denied (403)')

      // Check if we're already on a login page to avoid redirect loops
      const currentPath = window.location.pathname;
      if (currentPath.includes('/login') || currentPath.includes('/guest')) {
        return Promise.reject(error);
      }

      // Don't redirect on public or order-related pages - let them handle 403 gracefully
      if (currentPath.includes('/order-success/') ||
          currentPath.includes('/guest-order-tracking/') ||
          currentPath.includes('/orders/') ||
          currentPath.startsWith('/menu') ||
          currentPath.startsWith('/qr') ||
          currentPath.includes('/whatsapp-simulator')) {
        console.log('On public page, not redirecting on 403 - letting page handle gracefully');
        return Promise.reject(error);
      }

      // A 403 from the server means the user IS authenticated but does NOT
      // have permission for the resource — that is, an authorization
      // problem, not a session-expiry problem. Always let the calling
      // component render the backend's userMessage rather than redirecting
      // to /login (which loses the message and confuses the user with a
      // silent kick-out for, e.g., "Your account is not assigned to a
      // vendor"). Session-expiry signals come through 401, which is
      // handled above.
    }

    // Handle 500 Internal Server Error with better error messages
    if (error.response?.status === 500) {
      const errorData = error.response?.data;

      // Check if it's an AccessDeniedException
      if (typeof errorData === 'object' && errorData.message) {
        const message = errorData.message;

        // Extract meaningful error message from "Unexpected error: AccessDeniedException - <actual message>"
        if (message.includes('AccessDeniedException')) {
          const accessDeniedMatch = message.match(/AccessDeniedException\s*-\s*(.+)$/);
          if (accessDeniedMatch && accessDeniedMatch[1]) {
            // Replace the generic error with the specific message
            error.response.data = {
              ...errorData,
              message: accessDeniedMatch[1].trim(),
              error: 'Access Denied',
              originalMessage: message
            };
            console.error('🚫 Access Denied:', accessDeniedMatch[1].trim());
          }
        }
      }
    }

    console.error('❌ API Response Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

/**
 * Currency settings returned alongside auth responses.
 */
export interface AuthCurrency {
  code: string;
  symbol: string;
  locale: string;
}

/**
 * Shape of the backend JwtResponse contract returned from POST /auth/signin and
 * POST /auth/refresh-claims. The new finance-only-steward fields are optional
 * so that older bundles or unauthenticated paths still type-check.
 */
export interface JwtResponse {
  token: string;
  id: number;
  username: string;
  email: string;
  roles: string[];
  currency?: AuthCurrency;
  financeOnlySteward?: boolean;
  defaultLandingPath?: string | null;
}

/**
 * Calls POST /auth/refresh-claims to obtain an updated JwtResponse for the
 * currently-authenticated user. Useful when the vendor toggles a steward's
 * financeOnlyRestricted flag and we want the steward's UI to refresh without
 * forcing a logout.
 */
export const refreshAuthClaims = async (): Promise<JwtResponse> => {
  const response = await api.post('/auth/refresh-claims', {});
  return response.data as JwtResponse;
};

/**
 * Saves order preferences settings to the backend.
 * @param settings The settings object to save.
 */
export const saveOrderPreferences = async (settings: {
  autoAccept: boolean;
  allowCancellation: boolean;
  allowModification: boolean;
}) => {
  return api.post('/api/order-preferences', settings);
};

/**
 * Fetches QR codes for a specific vendor.
 * @param vendorId The ID of the vendor.
 * @returns A promise from the API GET request.
 */
export const fetchVendorQrCodes = async (vendorId: string) => {
  return api.get(`/api/vendors/${vendorId}/qr`);
};

/**
 * Creates a new QR code for a specific vendor.
 * @param vendorId The ID of the vendor.
 * @param qrCodeData The data for the new QR code.
 * @returns A promise from the API POST request.
 */
interface QrCodeData {
  location?: string;  // Backend uses location field
  metadata?: Record<string, string>;  // Backend expects metadata as key-value pairs
  logoUrl?: string | null;  // Optional logo URL
  [key: string]: unknown;
}

export const createVendorQrCode = async (vendorId: string, qrCodeData: QrCodeData) => {
  return api.post(`/api/vendors/${vendorId}/qr`, qrCodeData);
};

/**
 * Updates an existing QR code for a specific vendor.
 * @param vendorId The ID of the vendor.
 * @param qrCodeId The ID of the QR code to update.
 * @param qrCodeData The updated data for the QR code.
 * @returns A promise from the API PUT request.
 */
export const updateVendorQrCode = async (vendorId: string, qrCodeId: string, qrCodeData: QrCodeData) => {
  return api.put(`/api/vendors/${vendorId}/qr/${qrCodeId}`, qrCodeData);
};

/**
 * Deletes a QR code for a specific vendor.
 * @param vendorId The ID of the vendor.
 * @param qrCodeId The ID of the QR code to delete.
 * @returns A promise from the API DELETE request.
 */
export const deleteVendorQrCode = async (vendorId: string, qrCodeId: string) => {
  return api.delete(`/api/vendors/${vendorId}/qr/${qrCodeId}`);
};

/**
 * Fetches the QR code image for a specific vendor's QR code.
 * @param vendorId The ID of the vendor.
 * @param qrCodeCode The unique code of the QR code (not the database ID).
 * @returns A promise that resolves to the QR code image URL.
 */
export const fetchQrCodeImage = async (vendorId: string, qrCodeCode: string) => {
  const response = await api.get(`/api/vendors/${vendorId}/qr/${qrCodeCode}/image`, {
    responseType: 'blob'
  });
  return URL.createObjectURL(response.data);
};

/**
 * Fetches table occupancy status for the current vendor.
 */
export const getTableOccupancy = async () => {
  const response = await api.get('/api/tables/occupancy');
  return response.data;
};

/**
 * Fetches complete floor layout with table status.
 * @deprecated Use getFloorLayoutSummary for initial load instead
 */
export const getFloorLayout = async () => {
  const response = await api.get('/api/tables/layout');
  return response.data;
};

/**
 * Fetches optimized floor layout summary with aggregated counts only.
 * Use this for initial page load - much faster than getFloorLayout.
 */
export const getFloorLayoutSummary = async () => {
  const response = await api.get('/api/tables/layout-summary');
  return response.data;
};

/**
 * Fetches sections with tables for a specific floor (lazy loading).
 * Call this when a floor is expanded in the UI.
 */
export const getFloorSections = async (floorId: number | string) => {
  const response = await api.get(`/api/floors/${floorId}/sections`);
  return response.data;
};

/**
 * Fetches vendor table metrics with filtering and aggregation.
 * Returns hierarchical metrics at floor, section, and table levels.
 *
 * @param dateFilter - Date range filter (today, yesterday, recent, week, month, all)
 * @param orderTypes - Array of order types to filter by (TABLE_SERVICE, TAKEAWAY, DELIVERY, COUNTER_SERVICE)
 * @returns Vendor table metrics response with summary and detailed hierarchy
 */
export const getVendorTableMetrics = async (
  dateFilter: string = 'today',
  orderTypes: string[] = []
) => {
  const params = new URLSearchParams();
  params.append('dateFilter', dateFilter);
  orderTypes.forEach(type => params.append('orderTypes', type));

  const response = await api.get(`/api/vendor/tables/metrics?${params.toString()}`);
  return response.data;
};

/**
 * Fetches all tables for the current vendor.
 */
export const getTables = async () => {
  const response = await api.get('/api/tables');
  return response.data;
};

/**
 * Fetches tables for a specific section.
 */
export const getTablesBySection = async (sectionId: string) => {
  const response = await api.get(`/api/tables/section/${sectionId}`);
  return response.data;
};

/**
 * Creates a new table.
 */
export const createTable = async (tableData: { name: string; sectionId: string }) => {
  const response = await api.post('/api/tables', tableData);
  return response.data;
};

/**
 * Creates a new table with QR code in one operation.
 */
export const createTableWithQrCode = async (tableData: { name: string; sectionId: string }) => {
  const response = await api.post('/api/tables/with-qr', tableData);
  return response.data;
};

/**
 * Updates an existing table.
 */
export const updateTable = async (tableId: string, tableData: { name: string; sectionId?: string }) => {
  const response = await api.put(`/api/tables/${tableId}`, tableData);
  return response.data;
};

/**
 * Updates the display number for a table.
 */
export const updateTableDisplayNumber = async (tableId: string, displayNumber: string) => {
  const response = await api.put(`/api/tables/${tableId}/display-number`, { displayNumber });
  return response.data;
};

/**
 * Deletes a table.
 */
export const deleteTable = async (tableId: string) => {
  const response = await api.delete(`/api/tables/${tableId}`);
  return response.data;
};

/**
 * Assigns a QR code to a table.
 */
export const assignQrCodeToTable = async (tableId: string, qrCodeId: string) => {
  const response = await api.post(`/api/tables/${tableId}/assign-qr/${qrCodeId}`);
  return response.data;
};

/**
 * Removes QR code from a table.
 */
export const removeQrCodeFromTable = async (tableId: string) => {
  const response = await api.post(`/api/tables/${tableId}/remove-qr`);
  return response.data;
};

/**
 * Transfer all active orders from one table to another.
 * @param sourceTableId The ID of the source table
 * @param targetTableId The ID of the target table
 * @param reason Optional reason for the transfer
 * @returns Transfer response with details of transferred orders
 */
export const transferTableOrders = async (
  sourceTableId: string | number,
  targetTableId: string | number,
  reason?: string
) => {
  const response = await api.post(`/api/tables/${sourceTableId}/transfer`, {
    targetTableId: Number(targetTableId),
    reason
  });
  return response.data;
};

/**
 * Fetches all floors for the current vendor.
 */
export const getFloors = async () => {
  const response = await api.get('/api/floors');
  return response.data;
};

/**
 * Creates a new floor.
 */
export const createFloor = async (floorData: FloorRequest) => {
  const response = await api.post('/api/floors', floorData);
  return response.data;
};

/**
 * Updates an existing floor.
 */
export const updateFloor = async (floorId: string, floorData: FloorRequest) => {
  const response = await api.put(`/api/floors/${floorId}`, floorData);
  return response.data;
};

/**
 * Deletes a floor.
 */
export const deleteFloor = async (floorId: string) => {
  const response = await api.delete(`/api/floors/${floorId}`);
  return response.data;
};

/**
 * Multi-Menu Management API Functions
 */

/**
 * Fetches all menus for the current vendor.
 * @param includeDeleted - Optional parameter to include soft-deleted menus (for menu management page)
 */
export const getVendorMenus = async (includeDeleted: boolean = false) => {
  const response = await api.get('/api/menu-management/menus', {
    params: { includeDeleted }
  });
  return response.data;
};

/**
 * Creates a new menu.
 */
export const createMenu = async (menuData: {
  name: string;
  description?: string;
  menuType: string;
  isActive?: boolean;
  isDefault?: boolean;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: string[];
}) => {
  const response = await api.post('/api/menu-management/menus', menuData);
  return response.data;
};

/**
 * Updates an existing menu.
 */
export const updateMenu = async (menuId: number, menuData: {
  name?: string;
  description?: string;
  menuType?: string;
  isActive?: boolean;
  isDefault?: boolean;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: string[];
}) => {
  const response = await api.put(`/api/menu-management/menus/${menuId}`, menuData);
  return response.data;
};

/**
 * Deletes a menu (soft delete).
 */
export const deleteMenu = async (menuId: number) => {
  const response = await api.delete(`/api/menu-management/menus/${menuId}`);
  return response.data;
};

/**
 * Restores a deleted menu.
 */
export const restoreMenu = async (menuId: number) => {
  const response = await api.post(`/api/menu-management/menus/${menuId}/restore`);
  return response.data;
};

/**
 * Permanently deletes a menu (requires confirmation).
 */
export const permanentlyDeleteMenu = async (menuId: number) => {
  const response = await api.delete(`/api/menu-management/menus/${menuId}/permanent`);
  return response.data;
};

/**
 * Assigns sections to a menu.
 */
export const assignMenuToSections = async (menuId: number, sectionIds: number[]) => {
  const response = await api.post(`/api/menu-management/menus/${menuId}/sections`, {
    sectionIds
  });
  return response.data;
};

/**
 * Gets menus for a specific section.
 */
export const getSectionMenus = async (sectionId: number) => {
  const response = await api.get(`/api/menu-management/sections/${sectionId}/menus`);
  return response.data;
};

/**
 * Gets menu items for a specific menu and section.
 */
export const getSectionMenuItems = async (sectionId: number, menuId: number) => {
  const response = await api.get(`/api/menu-management/sections/${sectionId}/menu/${menuId}/items`);
  return response.data;
};

/**
 * Gets all section-menu assignments for the vendor.
 * Returns a map of sectionId -> assigned menu (or null).
 */
export const getSectionMenuAssignments = async (): Promise<Record<number, {
  id: number;
  name: string;
  menuType: string | null;
  isDefault: boolean;
  availableFrom: string | null;
  availableTo: string | null;
  availableDays: string | null;
  schedule: string;
  isActiveNow: boolean;
} | null>> => {
  const response = await api.get('/api/menu-management/sections/menu-assignments');
  return response.data;
};

/**
 * Venue Section Management
 */

/**
 * Gets all sections for the current vendor.
 */
export const getVendorSections = async () => {
  const response = await api.get('/api/sections');
  return response.data;
};

/**
 * Creates a new section.
 */
export const createSection = async (sectionData: { name: string; floorId: string }) => {
  const response = await api.post('/api/sections', sectionData);
  return response.data;
};

/**
 * Update section (new signature with floorId).
 */
export const updateSection = async (sectionId: string, sectionData: { name: string; floorId: string }) => {
  const response = await api.put(`/api/sections/${sectionId}`, sectionData);
  return response.data;
};

/**
 * Logical delete floor with pending orders check.
 */
export const logicalDeleteFloor = async (floorId: string, forceCascade: boolean = false) => {
  const response = await api.delete(`/api/floors/${floorId}/logical?forceCascade=${forceCascade}`);
  return response.data;
};

/**
 * Logical delete section with pending orders check.
 */
export const logicalDeleteSection = async (sectionId: string, forceCascade: boolean = false) => {
  const response = await api.delete(`/api/sections/${sectionId}/logical?forceCascade=${forceCascade}`);
  return response.data;
};

/**
 * Logical delete table with pending orders check.
 */
export const logicalDeleteTable = async (tableId: string, forceCascade: boolean = false) => {
  const response = await api.delete(`/api/tables/${tableId}/logical?forceCascade=${forceCascade}`);
  return response.data;
};

/**
 * Restore a logically deleted floor
 */
export const restoreFloor = async (floorId: string) => {
  const response = await api.post(`/api/floors/${floorId}/restore`);
  return response.data;
};

/**
 * Restore a logically deleted section
 */
export const restoreSection = async (sectionId: string) => {
  const response = await api.post(`/api/sections/${sectionId}/restore`);
  return response.data;
};

/**
 * Restore a logically deleted table
 */
export const restoreTable = async (tableId: string) => {
  const response = await api.post(`/api/tables/${tableId}/restore`);
  return response.data;
};

/**
 * Permanently delete a floor (requires confirmation)
 */
export const permanentlyDeleteFloor = async (floorId: string, confirmation: string) => {
  const response = await api.delete(`/api/floors/${floorId}/permanent`, {
    data: { confirmation }
  });
  return response.data;
};

/**
 * Permanently delete a section (requires confirmation)
 */
export const permanentlyDeleteSection = async (sectionId: string, confirmation: string) => {
  const response = await api.delete(`/api/sections/${sectionId}/permanent`, {
    data: { confirmation }
  });
  return response.data;
};

/**
 * Permanently delete a table (requires confirmation)
 */
export const permanentlyDeleteTable = async (tableId: string, confirmation: string) => {
  const response = await api.delete(`/api/tables/${tableId}/permanent`, {
    data: { confirmation }
  });
  return response.data;
};

/**
 * Updates an existing section (legacy function).
 */
export const updateSectionLegacy = async (sectionId: string, sectionData: { name: string }) => {
  const response = await api.put(`/api/sections/${sectionId}`, sectionData);
  return response.data;
};

/**
 * Deletes a section.
 */
export const deleteSection = async (sectionId: string) => {
  const response = await api.delete(`/api/sections/${sectionId}`);
  return response.data;
};

/**
 * Fetches sections for a specific floor.
 */
export const getSectionsByFloor = async (floorId: string) => {
  const response = await api.get(`/api/sections/floor/${floorId}`);
  return response.data;
};


/**
 * Fetches unassigned QR codes for table assignment.
 */
export const getUnassignedQrCodes = async () => {
  const response = await api.get('/api/qr-codes/unassigned');
  return response.data;
};

/**
 * Fetches all active orders for kitchen display.
 */
export const getKitchenOrders = async () => {
  const response = await api.get('/api/kitchen/orders');
  return response.data;
};

/**
 * Updates order status from kitchen display.
 */
export const updateKitchenOrderStatus = async (orderId: number, status: string) => {
  const response = await api.put(`/kitchen/orders/${orderId}/status`, null, {
    params: { status }
  });
  return response.data;
};

/**
 * Fetches kitchen dashboard statistics.
 */
export const getKitchenStats = async () => {
  const response = await api.get('/api/kitchen/stats');
  return response.data;
};

export interface Floor {
  id: string;
  name: string;
  floorNumber: number;
  vendorId?: string;
}

export interface Section {
  id: string;
  name: string;
  floorId: string;
  floor?: Floor;
}

export interface QrCode {
  id: string;
  code: string;
  location?: string;
  url?: string;
}

export interface VenueTable {
  id: string;
  name: string;
  displayNumber?: string;
  sectionId: string;
  section?: Section;
  qrCode?: QrCode;
}

export interface FloorRequest {
  name: string;
  floorNumber: number;
}

export interface SectionRequest {
  name: string;
  floorId: string;
}

export interface VenueTableRequest {
  name: string;
  sectionId: string;
}

// OrderLine API endpoints
export const orderLineApi = {
  // Get OrderLine details by session code
  getOrderLine: (sessionCode: string) => 
    api.get(`/api/order-lines/${sessionCode}`),
  
  // Get active OrderLine for a QR code
  getActiveSessionByQrCode: (qrCode: string) => 
    api.get(`/api/order-lines/qr/${qrCode}`),
  
  // Get all active OrderLines for vendor
  getActiveVendorSessions: () => 
    api.get('/api/order-lines/vendor/active'),
    
  // Get all vendor sessions with optional status filter
  getVendorSessions: (status?: string) => 
    api.get('/api/order-lines/vendor/sessions', { 
      params: status ? { status } : {} 
    }),
  
  // Request bill for an OrderLine
  requestBill: (sessionCode: string) => 
    api.post(`/api/order-lines/${sessionCode}/request-bill`),
  
  // Update OrderLine status (steward only)
  updateOrderLineStatus: (orderLineId: number, status: string) => 
    api.put(`/api/order-lines/${orderLineId}/status`, { status }),
  
  // Process payment for OrderLine
  processPayment: (orderLineId: number, amount: number) => 
    api.post(`/api/order-lines/${orderLineId}/payment`, { amount })
};

// Steward Management APIs (Admin)
export const getStewards = async () => {
  const response = await api.get('/api/admin/stewards');
  return response.data;
};

export const createSteward = async (stewardData: {
  name: string;
  email: string;
  stewardCode: string;
  password: string;
  isActive: boolean;
  vendorId?: number; // Optional: for admins creating stewards for specific vendors
  financeOnlyRestricted?: boolean; // Restrict steward to Income/Expense/EOD add-only access
}) => {
  // Create steward (admin route)
  const response = await api.post('/api/admin/stewards', stewardData);
  return response.data;
};

export const updateSteward = async (stewardId: number, stewardData: {
  name: string;
  email: string;
  stewardCode: string;
  password?: string;  // Deprecated - use updateStewardPassword instead
  isActive: boolean;
}) => {
  // Update steward (admin route)
  const response = await api.put(`/api/admin/stewards/${stewardId}`, stewardData);
  return response.data;
};

export const updateStewardPassword = async (stewardId: number, passwordData: {
  password: string;
  confirmPassword: string;
}) => {
  const response = await api.put(`/api/admin/stewards/${stewardId}/password`, passwordData);
  return response.data;
};

export const deleteSteward = async (stewardId: number) => {
  const response = await api.delete(`/api/admin/stewards/${stewardId}`);
  return response.data;
};

export const getStewardAssignments = async (stewardId: number) => {
  const response = await api.get(`/api/admin/stewards/${stewardId}/assignments`);
  return response.data;
};

export const updateStewardAssignments = async (stewardId: number, tableIds: number[]) => {
  const response = await api.post(`/api/admin/stewards/${stewardId}/assignments`, { tableIds });
  return response.data;
};

// Vendor Steward Management APIs (Vendor)
export const getVendorStewards = async () => {
  const response = await api.get('/api/vendor/stewards');
  return response.data;
};

export const getVendorTablesForAssignment = async (stewardId: number) => {
  const response = await api.get(`/api/vendor/tables-for-assignment?stewardId=${stewardId}`);
  // Backend now returns flat array of tables with isAssigned property
  return response.data;
};

export const assignVendorTablesToSteward = async (stewardId: number, tableIds: number[]) => {
  const response = await api.post(`/api/vendor/stewards/${stewardId}/assign-tables`, { tableIds });
  return response.data;
};

// Kitchen Management APIs
export const getKitchens = async () => {
  const response = await api.get('/api/kitchens');
  return response.data;
};

export const getAllKitchens = async () => {
  const response = await api.get('/api/kitchens/all');
  return response.data;
};

export const getDefaultKitchen = async () => {
  const response = await api.get('/api/kitchens/default');
  return response.data;
};

export const getKitchen = async (kitchenId: number) => {
  const response = await api.get(`/api/kitchens/${kitchenId}`);
  return response.data;
};

export const createKitchen = async (kitchenData: {
  name: string;
  type: string;
  description?: string;
  isActive: boolean;
  printerId?: number;
}) => {
  const response = await api.post('/api/kitchens', kitchenData);
  return response.data;
};

export const updateKitchen = async (kitchenId: number, kitchenData: {
  name: string;
  type: string;
  description?: string;
  isActive: boolean;
  printerId?: number;
}) => {
  const response = await api.put(`/api/kitchens/${kitchenId}`, kitchenData);
  return response.data;
};

export const deleteKitchen = async (kitchenId: number) => {
  const response = await api.delete(`/api/kitchens/${kitchenId}`);
  return response.data;
};

export const getKitchenMenuItems = async (kitchenId: number) => {
  const response = await api.get(`/api/kitchens/${kitchenId}/menu-items`);
  return response.data;
};

export const assignMenuItemsToKitchen = async (kitchenId: number, menuItemIds: number[]) => {
  const response = await api.post(`/api/kitchens/${kitchenId}/menu-items`, { menuItemIds });
  return response.data;
};

// Printer Management APIs
export const getPrinters = async () => {
  const response = await api.get('/api/printer-management/printers');
  return response.data;
};

export const getPrinter = async (printerId: number) => {
  const response = await api.get(`/api/printer-management/printers/${printerId}`);
  return response.data;
};

export const createPrinter = async (printerData: {
  name: string;
  ipAddress: string;
  port: number;
  type: string;
  isActive: boolean;
  isDefault: boolean;
}) => {
  const response = await api.post('/api/printer-management/printers', printerData);
  return response.data;
};

export const updatePrinter = async (printerId: number, printerData: {
  name: string;
  ipAddress: string;
  port: number;
  type: string;
  isActive: boolean;
  isDefault: boolean;
}) => {
  const response = await api.put(`/api/printer-management/printers/${printerId}`, printerData);
  return response.data;
};

export const deletePrinter = async (printerId: number) => {
  const response = await api.delete(`/api/printer-management/printers/${printerId}`);
  return response.data;
};

export const testPrinter = async (printerId: number) => {
  const response = await api.post(`/api/printer-management/printers/${printerId}/test`);
  return response.data;
};

export const setDefaultPrinter = async (printerId: number) => {
  const response = await api.post(`/api/printer-management/printers/${printerId}/set-default`);
  return response.data;
};

// KOT Management APIs
export const getKOTsByKitchen = async (kitchenId: number) => {
  const response = await api.get(`/api/kot/kitchen/${kitchenId}`);
  return response.data;
};

export const getKOTsByOrder = async (orderId: number) => {
  const response = await api.get(`/api/kot/order/${orderId}`);
  return response.data;
};

export const updateKOTStatus = async (kotId: number, status: string) => {
  const response = await api.put(`/api/kot/${kotId}/status`, { status });
  return response.data;
};

export const updateKOTItemStatus = async (itemId: number, status: string) => {
  const response = await api.put(`/api/kot/item/${itemId}/status`, { status });
  return response.data;
};

export const getActiveKOTCounts = async () => {
  const response = await api.get('/api/kot/counts');
  return response.data;
};

/**
 * Gets sections assigned to a specific menu.
 */
export const getMenuSections = async (menuId: number) => {
  const response = await api.get(`/api/menu-management/menus/${menuId}/sections`);
  return response.data;
};

/**
 * Menu Item Topping Management API Functions
 */

export interface ToppingRequest {
  name: string;
  price: number;
  description?: string;
  isAvailable?: boolean;
  displayOrder?: number;
}

/**
 * Get all toppings for a menu item
 */
export const getMenuItemToppings = async (menuItemId: number) => {
  const response = await api.get(`/api/menu-items/${menuItemId}/toppings`);
  return response.data;
};

/**
 * Add a new topping to a menu item
 */
export const addTopping = async (menuItemId: number, data: ToppingRequest) => {
  const response = await api.post(`/api/menu-items/${menuItemId}/toppings`, data);
  return response.data;
};

/**
 * Update an existing topping
 */
export const updateTopping = async (toppingId: number, data: ToppingRequest) => {
  const response = await api.put(`/api/menu-items/toppings/${toppingId}`, data);
  return response.data;
};

/**
 * Delete a topping
 */
export const deleteTopping = async (toppingId: number) => {
  const response = await api.delete(`/api/menu-items/toppings/${toppingId}`);
  return response.data;
};



/**
 * Search menu items within a specific menu context (via QR code)
 * @param qrCode - The QR code identifying the table/section
 * @param query - The search query string
 * @returns Search results with menu items matching the query
 */
export const searchMenuItemsByQrCode = async (qrCode: string, query: string) => {
  const response = await api.get(`/qr/${qrCode}/menu/search`, {
    params: { query }
  });
  return response.data;
};

// Customer Request APIs
export const customerRequestApi = {
  create: (data: CreateCustomerRequestData) => api.post('/api/customer-requests', data),
  
  getActiveByVendor: (vendorId: number) => 
    api.get(`/api/customer-requests/vendor/${vendorId}/active`),
  
  getPendingCount: (vendorId: number) => 
    api.get(`/api/customer-requests/vendor/${vendorId}/pending-count`),
    
  getById: (requestId: number) => 
    api.get(`/api/customer-requests/${requestId}`)
};

// Steward Action APIs
export const stewardApi = {
  claimRequest: (requestId: number) => 
    api.post('/api/steward/claim', { requestId }),
    
  completeRequest: (requestId: number, notes?: string) => 
    api.post('/api/steward/complete', { requestId, notes }),
    
  getPerformance: (vendorId: number, days: number = 7) => 
    api.get(`/api/steward/performance?vendorId=${vendorId}&days=${days}`),
    
  getMyRequests: () => 
    api.get('/api/steward/my-requests')
};

// Timeline APIs
export const timelineApi = {
  getVendorTimeline: (vendorId: number, params?: {
    tableId?: number;
    orderId?: number;
    eventType?: string;
    page?: number;
    size?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return api.get(`/api/timeline/vendor/${vendorId}?${queryParams.toString()}`);
  }
};

// Auth APIs for password reset
export const authApi = {
  // Request password reset (sends OTP to email)
  forgotPassword: (emailOrUsername: string) =>
    api.post('/auth/forgot-password', {
      emailOrUsername,
      method: 'otp'
    }),

  // Reset password using OTP
  resetPasswordWithOTP: (email: string, otp: string, newPassword: string) =>
    api.post('/auth/reset-password/otp', {
      email,
      otp,
      newPassword
    }),

  // Resend verification OTP
  resendVerificationOTP: (email: string) =>
    api.post('/auth/resend-verification', {
      email,
      method: 'otp'
    }),

  // Verify email with OTP (for registration)
  verifyEmailWithOTP: (email: string, otp: string) =>
    api.post('/auth/verify-email/otp', {
      email,
      otp
    })
};

// Customer Orders API
export const customerOrdersApi = {
  /**
   * Get paginated orders for the current customer
   * @param params - Pagination and filter parameters
   * @returns Paginated order response
   */
  getOrders: (params?: {
    page?: number;
    size?: number;
    dateFilter?: string; // 'all', 'today', 'recent', 'week', 'month'
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const queryString = queryParams.toString();
    return api.get(`/orders${queryString ? `?${queryString}` : ''}`);
  }
};

// Floor plan position APIs
export const batchUpdateTablePositions = (positions: Array<{ tableId: number; x: number; y: number; width?: number; height?: number; rotation?: number }>) =>
  api.put('/api/tables/batch-positions', { positions });

export const updateSectionBoundary = (sectionId: number, boundary: { x: number; y: number; width: number; height: number }) =>
  api.put(`/api/sections/${sectionId}/boundary`, boundary);

// Floor asset APIs
export const getFloorAssets = (floorId: number) =>
  api.get(`/api/floors/${floorId}/assets`);

export const createFloorAsset = (floorId: number, asset: { assetType: string; label?: string; positionX: number; positionY: number; width: number; height: number; rotation: number }) =>
  api.post(`/api/floors/${floorId}/assets`, asset);

export const updateFloorAsset = (assetId: number, data: { positionX?: number; positionY?: number; width?: number; height?: number; rotation?: number; label?: string }) =>
  api.put(`/api/floor-assets/${assetId}`, data);

export const deleteFloorAsset = (assetId: number) =>
  api.delete(`/api/floor-assets/${assetId}`);

export const batchUpdateFloorAssetPositions = (positions: Array<{ id: number; x: number; y: number; width?: number; height?: number; rotation?: number }>) =>
  api.put('/api/floor-assets/batch-positions', positions);

// ── Promotions ──────────────────────────────────────────────────────────
// Offers that apply automatically when their conditions are met: happy hours,
// percentage and flat discounts, scoped by day, time and channel. `liveNow` on
// each row is computed server-side against the VENDOR's timezone — the browser
// must not recompute it from local time, or a vendor viewing from another
// country sees the wrong answer.

/**
 * Every kind of offer a restaurant runs, on one model.
 *
 * The list is grouped by how the guest MEETS the offer, which is also how the
 * Loyalty Programs page groups its tiles:
 *
 *   at the till       HAPPY_HOUR, PERCENT_OFF, FLAT_OFF, BOGO, COMBO, ITEM_FREE
 *   earned over time  STAMP_CARD, POINTS, SPEND_GOAL, CASHBACK
 *   fired by an event BIRTHDAY, REFERRAL, WELCOME, WIN_BACK
 *
 * A coupon is not a type — it is any of the above with a `code` on it. That is
 * why "20% off" and "20% off with code SAVE20" are one offer with one setting
 * changed, rather than two things to build and maintain separately.
 */
export type PromoType =
  | 'HAPPY_HOUR' | 'PERCENT_OFF' | 'FLAT_OFF' | 'ITEM_FREE' | 'COMBO' | 'BOGO'
  | 'STAMP_CARD' | 'POINTS' | 'SPEND_GOAL' | 'CASHBACK' | 'TIER'
  | 'BIRTHDAY' | 'REFERRAL' | 'WELCOME' | 'WIN_BACK';

export interface Promotion {
  id: number;
  name: string;
  description: string | null;
  promoType: PromoType;
  scope: 'CHECK' | 'ITEM';
  percentage: number | null;
  amount: number | null;
  maxDiscountAmount: number | null;
  minOrderAmount: number | null;
  isActive: boolean;
  priority: number;
  nonExclusive: boolean;
  startsOn: string | null;
  endsOn: string | null;
  daysOfWeek: string | null;   // "MON,TUE,WED" — null means every day
  startTime: string | null;    // "16:00:00" — null means all day
  endTime: string | null;
  channels: string | null;     // "TABLE_SERVICE,TAKEAWAY" — null means all

  // Coupon — a code turns an automatic offer into one that must be asked for.
  code: string | null;
  maxRedemptions: number | null;   // across everyone; null = unlimited
  maxPerCustomer: number | null;   // null = unlimited
  redemptionsUsed: number;

  // Earn conditions. Which of these matters depends on promoType.
  requiredVisits: number | null;
  requiredSpend: number | null;
  requiredItemCount: number | null;
  qualifyingItemId: number | null;
  qualifyingItemName: string | null;
  /**
   * Points per UNIT of currency — one pound or one rupee, not per rupee. The
   * older loyalty model is rupee-calibrated, which is why it is switched off for
   * GB vendors; this is not, so the same offer works in either country.
   */
  pointsPerUnit: number | null;
  pointsToValue: number | null;
  /** Fewest points spendable in one go. Null means no floor. */
  minRedeemPoints: number | null;
  /** BIRTHDAY: days either side of the date. WIN_BACK: days of absence. */
  triggerWindowDays: number | null;
  freeItemId: number | null;
  freeItemName: string | null;
  rewardValidityDays: number | null;
  triggerEvent: 'BIRTHDAY' | 'REFERRAL' | 'SIGNUP' | 'LAPSED' | null;
  displayOrder: number;

  isCoupon: boolean;
  isEarned: boolean;
  isTriggered: boolean;
  liveNow: boolean;
}

export type PromotionInput = Partial<
  Omit<Promotion, 'id' | 'liveNow' | 'isCoupon' | 'isEarned' | 'isTriggered'
       | 'redemptionsUsed' | 'qualifyingItemName' | 'freeItemName' | 'triggerEvent'>
>;

/** Just enough of a menu item to fill a picker. */
export interface PromotionMenuItem {
  id: number;
  name: string;
  price: number | null;
}

export const getPromotions = () => api.get<Promotion[]>('/api/promotions');

export const getLivePromotions = (channel?: string) =>
  api.get<Promotion[]>('/api/promotions/live', { params: channel ? { channel } : {} });

export const getPromotionMenuItems = () =>
  api.get<PromotionMenuItem[]>('/api/promotions/menu-items');

export const createPromotion = (data: PromotionInput) =>
  api.post<Promotion>('/api/promotions', data);

export const updatePromotion = (id: number, data: PromotionInput) =>
  api.put<Promotion>(`/api/promotions/${id}`, data);

export const togglePromotion = (id: number) =>
  api.post<Promotion>(`/api/promotions/${id}/toggle`, {});

export const deletePromotion = (id: number) =>
  api.delete(`/api/promotions/${id}`);

/** Does this code work on a bill of this size? Changes nothing. */
export const checkCouponCode = (data: {
  code: string; subtotal: number; channel?: string; customerId?: number;
}) => api.post<{
  valid: boolean;
  discount: number;
  message: string | null;
  promotion: Promotion | null;
}>('/api/promotions/check-code', data);

// ── Offers on a bill ────────────────────────────────────────────────────
// What a live offer is worth on THIS bill, and putting it on. The decision is
// made server-side so the floor-plan screen and the orders screen cannot
// disagree about what is on offer or what it is worth.

export interface BillOffer {
  promotionId: number;
  name: string;
  promoType: PromoType;
  /** For the label only — the amount below is already capped. */
  percentage: number | null;
  amount: number | null;
}

export interface BillOffers {
  orderLineId: number;
  sessionCode: string;
  subtotal: number;
  channel: string | null;
  available: boolean;
  /** Discount on the PRE-TAX subtotal, already capped. Apply as FIXED. */
  discountAmount: number;
  alreadyDiscounted: boolean;
  offers: BillOffer[];
}

/** Read-only. Safe to call while a guest is still ordering. */
export const getBillOffers = (orderLineId: number) =>
  api.get<BillOffers>(`/api/order-lines/${orderLineId}/offers`);

/**
 * Put the best automatic offer on this bill.
 *
 * Sends no amount: what applies is worked out again on the server, because the
 * figure the browser was shown may already be stale — the happy hour may have
 * ended, or another till may have taken a coupon's last use.
 */
export const applyBillOffers = (orderLineId: number) =>
  api.post(`/api/order-lines/${orderLineId}/offers/apply`, {});

// ── What the guest has earned so far ────────────────────────────────────
// Stamp cards, points, spend goals, tiers and cashback build up across visits
// rather than applying to one bill. Shown while the guest is still at the
// table, because "one more visit and dessert is on us" is worth something
// there and nothing at all on a statement a fortnight later.

export interface LoyaltyStanding {
  promotionId: number;
  name: string;
  promoType: PromoType;
  /**
   * Which of three shapes this is, because each is told to a guest in a
   * completely different sentence:
   *
   *  PROGRESS — working towards something: "two more visits"
   *  BALANCE  — points or cashback in hand: "you have ₹135 to spend"
   *  TIER     — standing they hold: "you're a Gold guest"
   */
  kind: 'PROGRESS' | 'BALANCE' | 'TIER';
  /** True when the guest holds this tier. Only meaningful for a TIER. */
  tierHeld: boolean;
  /** What the balance is worth in money. Only set for a BALANCE. */
  balanceWorth: number | null;
  /** Fewest points spendable in one go, or null for no floor. */
  minRedeem: number | null;
  /** What is being counted, so the caller knows to format money as money. */
  unit: 'visits' | 'spend' | 'items' | 'points' | 'credit';
  have: number;
  /** Null for points and cashback: a balance, not a race. */
  target: number | null;
  remaining: number | null;
  percent: number;
  /** The reward is earned and still in date — tell the guest now. */
  claimable: boolean;
  expiresOn: string | null;
  /** What the reward is worth on THIS bill. Null until earned. */
  rewardValue: number | null;
  /** What it gives, to be read out: "Vegetarian Pasta free". */
  rewardLabel: string | null;
  /** Earned, but not givable here — "the guest has not ordered it". */
  blockedReason: string | null;
}

export interface BillLoyalty {
  orderLineId: number;
  /** False when nobody is attached to the bill — no number was taken. */
  recognised: boolean;
  /** This bill has already been counted; settling it again adds nothing. */
  alreadyCounted: boolean;
  /**
   * What a reward would replace. A bill carries ONE discount, so a guest with
   * a full card who came in during happy hour has to choose between them.
   */
  currentDiscount: { amount: number; reason: string } | null;
  standings: LoyaltyStanding[];
}

/** Read-only. Nothing here counts a visit; only settling the bill does. */
export const getBillLoyalty = (orderLineId: number) =>
  api.get<BillLoyalty>(`/api/order-lines/${orderLineId}/loyalty`);

/**
 * Give the guest a reward they have earned.
 *
 * Sends no figure: the server prices it from the offer and the bill, so a till
 * can never name its own amount.
 *
 * `replaceExisting` is the guest's answer, not a convenience flag. Without it
 * the server refuses when the bill already has a discount, and says so.
 * Reversible either way — removing the discount puts the visits back.
 */
export const giveReward = (orderLineId: number, promotionId: number, replaceExisting: boolean) =>
  api.post<{ discountAmount: number; rewardLabel: string; finalAmount: number }>(
    `/api/order-lines/${orderLineId}/loyalty/${promotionId}/claim`, { replaceExisting });

/**
 * Spend a guest's points or cashback against this bill.
 *
 * A different act from claiming a reward, hence a different endpoint. A stamp
 * card is earned and taken whole; a balance is closer to money the guest
 * already holds and is spent in whatever amount suits — 200 of their 500
 * points today, the rest another time.
 *
 * Omit `points` to spend as much as the bill can absorb. The server decides
 * what that is worth; a till naming its own figure would make the vendor's
 * rate advisory. Reversible — removing the discount puts the points back.
 */
export const spendBalance = (
  orderLineId: number, promotionId: number,
  opts: { points?: number; replaceExisting?: boolean } = {},
) =>
  api.post<{ discountAmount: number; spent: string; finalAmount: number }>(
    `/api/order-lines/${orderLineId}/loyalty/${promotionId}/spend`, opts);

// ── Vouchers ────────────────────────────────────────────────────────────
// Stored value spent against a bill. Two kinds, and the difference is tax:
// PROMO_CREDIT is given away, GIFT_CARD is sold. The treatment is decided by
// the server at issue and stamped on the voucher, because it depends on the
// vendor's country and must not change under an already-sold card.

export interface Voucher {
  id: number;
  code: string;
  voucherType: 'GIFT_CARD' | 'PROMO_CREDIT';
  initialBalance: number;
  remainingBalance: number;
  soldFor: number | null;
  taxTreatment: 'VAT_AT_ISSUE' | 'TAX_AT_REDEMPTION' | 'NOT_A_SUPPLY';
  expiresOn: string | null;
  isActive: boolean;
  expired: boolean;
  spendable: boolean;
}

export interface VoucherTxn {
  type: 'ISSUE' | 'REDEEM' | 'REFUND' | 'EXPIRE' | 'ADJUST';
  amount: number;
  balanceAfter: number;
  orderId: number | null;
  notes: string | null;
  at: string | null;
}

export const getVouchers = () => api.get<Voucher[]>('/api/vouchers');

/** Unspent voucher value the vendor is holding. A liability, not income. */
export const getVoucherLiability = () =>
  api.get<{ outstanding: number }>('/api/vouchers/liability');

export const issueVoucher = (data: {
  voucherType: 'GIFT_CARD' | 'PROMO_CREDIT';
  faceValue: number;
  soldFor?: number | null;
  issuedToCustomerId?: number | null;
  expiresOn?: string | null;
  code?: string | null;
}) => api.post<Voucher>('/api/vouchers', data);

export const checkVoucher = (data: { code: string; billTotal?: number }) =>
  api.post<{
    valid: boolean;
    applies: number;
    message: string | null;
    voucher: Voucher | null;
  }>('/api/vouchers/check', data);

export const getVoucherHistory = (id: number) =>
  api.get<VoucherTxn[]>(`/api/vouchers/${id}/history`);

export const deactivateVoucher = (id: number) =>
  api.post<Voucher>(`/api/vouchers/${id}/deactivate`, {});

export default api